import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthCurator } from "@src/hooks/useAuth";
import { useCuratorConversationEvents } from "@src/hooks/useCuratorConversationEvents";

type CuratorConversationsContextValue = {
  isEventsConnected: boolean;
  refreshConversations: () => void;
};

const CuratorConversationsContext =
  createContext<CuratorConversationsContextValue>({
    isEventsConnected: false,
    refreshConversations: () => undefined,
  });

export const CuratorConversationsProvider = ({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element => {
  const curator = useAuthCurator();
  const queryClient = useQueryClient();

  const { isEventsConnected } = useCuratorConversationEvents(
    curator?.documentId,
    !!curator?.documentId
  );

  const refreshConversations = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
  }, [queryClient]);

  const value = useMemo(
    () => ({ isEventsConnected, refreshConversations }),
    [isEventsConnected, refreshConversations]
  );

  return (
    <CuratorConversationsContext.Provider value={value}>
      {children}
    </CuratorConversationsContext.Provider>
  );
};

export const useCuratorConversationsContext = () =>
  useContext(CuratorConversationsContext);
